import { GraphQLOptions, GraphQLResult, GraphqlSubscriptionResult } from '@aws-amplify/api-graphql';
import { generateClient } from 'aws-amplify/api';
import * as Sentry from '@sentry/react';
import {
  sendApplicationError,
  sendSystemError,
  FINGERPRINT_APPLICATION_ERROR,
  FINGERPRINT_SYSTEM_ERROR,
  createCustomError
} from '@/lib/sentryClient';
import { GraphQLError } from 'graphql/error/GraphQLError';

/**
 * NOTE:
 * 以下処理は既存実装のエラーハンドリングに合わせてエラーが起きた場合に関数内で処理せずにエラーを呼び出し元に投げるようにしている。
 * 理想はエラーハンドリングは関数内で行い呼び出し元にはエラーを返さないようにしたい。
 * 理由としては関数内でカスタムするためにSentryにログを送信しているが、呼び出しもとにエラーを伝搬させることで
 * 呼び出し元でも他処理と相まってSentryにログを送る関係で2重になってしまうため。
 * なので現状は呼び出し元でError.nameがCUSTOM_ERROR_HANDLEの場合はSentryにログを送らないように制御している
 * 以下のような型でレスポンスを返すイメージ
 * export type Customrror = {
    code: number;
    error: Error;
   };
   export type Result<T> = {
    data?: T;
    error?: CustomError;
};
*/
const client = generateClient();

export const executeQuery = async <T>(
  query: string,
  variables?: Record<string, any>,
  authMode?: GraphQLOptions['authMode']
): Promise<T> => {
  const operation = getOperationName(query);
  const operationName = operation?.operationName ?? 'unknown Operation';
  try {
    const res = (await client.graphql({
      query,
      variables,
      authMode
    })) as GraphQLResult<any>;

    if (res.errors && res.errors.length > 0) {
      const e = createCustomError(res.errors[0]);
      e.name = `${operationName} ${e.name}`;
      e.message = `failed to ${operationName} ${FINGERPRINT_APPLICATION_ERROR} : ${e.message}`;

      const extra = {
        extra: {
          isCustomEvent: true,
          Query: {
            operationName: operationName,
            request: JSON.stringify(variables),
            operation: operation?.operation ?? 'unknown',
            response: JSON.stringify(res.data),
            extentions: JSON.stringify(res.extensions)
          }
        }
      };

      // 最初のエラー以外はパンくずリストに残す
      if (res.errors.length > 1) {
        sendErrorToBreadClumbs(res.errors.slice(1));
      }

      sendApplicationError(e, operationName, extra);
      throw e;
    }

    Sentry.addBreadcrumb({
      category: 'GraphQL',
      message: `${operationName} executed`,
      data: {
        request: JSON.stringify(variables),
        response: JSON.stringify(res.data)
      }
    });

    return res.data;
  } catch (e) {
    const extra = {
      Query: {
        operationName: operationName,
        request: JSON.stringify(variables),
        operation: operation?.operation ?? 'unknown'
      }
    };

    const message = `failed to ${operationName} ${FINGERPRINT_SYSTEM_ERROR}`;
    if (e instanceof Error) {
      e.name = `${operationName} ${e.name}`;
      e.message = `${message} ${e.message}`;
      sendSystemError(e, operationName, extra);
      throw e;
    }

    const error = createCustomError(new Error(message));
    error.name = `${operationName} ${error.name}`;
    sendSystemError(error, operationName, extra);
    throw error;
  }
};

export const executeSubscribe = <T>(
  query: string,
  next: (value: T) => void,
  variables?: Record<any, any>,
  errorFunc?: (error: any) => void,
  authMode?: GraphQLOptions['authMode']
) => {
  try {
    const operation = getOperationName(query);
    const operationName = operation?.operationName ?? 'unknown Operation';

    const observable = client.graphql({
      query,
      variables,
      authMode
    }) as GraphqlSubscriptionResult<GraphQLResult<T>>;

    return observable.subscribe({
      next: (result) => {
        if (result.data.errors) {
          const e = result.data.errors[0];
          e.message = `failed to ${operationName} ${FINGERPRINT_APPLICATION_ERROR} : ${e.message}`;
          e.name = `${operationName} subscrive failed ${e.name}`;
          const extra = {
            Subscription: {
              operationName: operationName,
              operation: operation?.operation,
              extentions: JSON.stringify(result.data.extensions)
            }
          };

          // 最初のエラー以外はパンくずリストに残す
          if (result.data.errors.length > 1) {
            sendErrorToBreadClumbs(result.data.errors.slice(1));
          }

          sendApplicationError(e, operationName, extra);
        } else {
          next(result.data.data);
        }

        Sentry.addBreadcrumb({
          category: 'GraphQL',
          message: `${operationName} next executed`,
          data: {
            receiveData: JSON.stringify(result.data)
          }
        });
      },
      error: (e) => {
        if (errorFunc) {
          errorFunc(e);
        }

        const message = `failed to subscribe ${operationName} ${FINGERPRINT_SYSTEM_ERROR}`;
        const extra = {
          extra: {
            Subscription: {
              operationName: operationName,
              operation: 'ovserve'
            }
          }
        };

        if (e instanceof Error) {
          e.name = `${operationName} ovserve failed ${e.name}`;
          e.message = `${message}: ${e.message}`;
          sendSystemError(e, operationName, extra);
          throw e;
        }

        const error = createCustomError(new Error(message));
        error.name = `${operationName} ovserve failed ${error.name}`;

        sendSystemError(error, operationName, extra);
        throw e;
      },
      complete: () => {
        Sentry.addBreadcrumb({
          category: 'graphql',
          message: `${operationName} completed`,
          level: 'info'
        });
      }
    });
  } catch (e) {
    const operation = getOperationName(query);
    const operationName = operation?.operationName ?? 'unknown Operation';
    const extra = {
      Subscription: {
        operationName: operationName,
        request: JSON.stringify(variables),
        operation: operation?.operation ?? 'unknown'
      }
    };

    const message = `failed to ${operationName} ${FINGERPRINT_SYSTEM_ERROR}`;

    if (e instanceof Error) {
      e.name = `${operationName} ${e.name}`;
      e.message = `${message}: ${e.message}`;
      sendSystemError(e, operationName, extra);
      throw e;
    }

    const error = createCustomError(new Error(message));
    error.name = `${operationName} ${error.name}`;

    sendSystemError(error, operationName, extra);
    throw error;
  }
};

type Operation = {
  operation: string;
  operationName: string;
};
// 操作名を取得する関数
const getOperationName = (query: string): Operation | null => {
  const match = query.match(/(query|mutation|subscription)\s+(\w+)/);
  if (match && match[2]) {
    return {
      operation: match[1],
      operationName: match[2]
    };
  }
  return null;
};

// GraphQLから返却されるエラーの中で先頭以外のエラーをパンくずリストに出力する
const sendErrorToBreadClumbs = (errors: GraphQLError[]) => {
  errors.forEach((error: GraphQLError) => {
    Sentry.addBreadcrumb({
      category: 'GraphQL.Error',
      message: error.message,
      data: {
        name: error.name,
        path: error.path,
        extentions: JSON.stringify(error.extensions)
      }
    });
  });
};
